import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import type { QueryClient } from '@tanstack/react-query';
import type { TRPCOptionsProxy } from '@trpc/tanstack-react-query';
import { BULK_OPERATIONS } from '@seo/contracts';
import type { AppRouter } from '@seo/api/src/routers';
import { AppShell } from './components/AppShell';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { TotpSetupPage } from './pages/TotpSetupPage';
import { DashboardPage } from './pages/DashboardPage';
import { GeoPage } from './pages/GeoPage';
import { ReferencePage } from './pages/ReferencePage';
import { BrandsPage } from './pages/BrandsPage';
import { CorePage } from './pages/CorePage';
import { GamesPage } from './pages/GamesPage';
import { ClustersPage } from './pages/ClustersPage';
import { SitesPage } from './pages/SitesPage';
import { SiteCardPage } from './pages/SiteCardPage';
import { AuditPage } from './pages/AuditPage';
import { BuildPage } from './pages/BuildPage';
import { EditorPage } from './pages/EditorPage';
import { BulkPage } from './pages/BulkPage';
import { MonitoringPage } from './pages/MonitoringPage';
import { LinksPage } from './pages/LinksPage';
import { EmdPage } from './pages/EmdPage';
import { SettingsPage } from './pages/SettingsPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { PromptsPage } from './pages/PromptsPage';
import { LinkContentPage } from './pages/LinkContentPage';
import { NewSitePage } from './pages/NewSitePage';
import { UsersPage } from './pages/UsersPage';
import type { SessionUser } from './lib/session';

export interface RouterContext {
  queryClient: QueryClient;
  trpc: TRPCOptionsProxy<AppRouter>;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});

const setPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/set-password',
  component: SetPasswordPage,
});

/**
 * Forced TOTP enrolment for a signed-in `super_admin` who has not set up a second factor.
 *
 * Public sign-out aside, this route needs a session just like `authedRoute` — but it must
 * stay reachable even when `twoFactorSetupRequired` is true, which is exactly the state
 * `authedRoute.beforeLoad` below redirects *away from*. Hence its own guard rather than
 * nesting under `authedRoute`.
 */
const setup2faRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/setup-2fa',
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient
      .fetchQuery(context.trpc.session.me.queryOptions())
      .catch(() => null);
    if (!session) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
    return { session: session as SessionUser };
  },
  component: TotpSetupPage,
});

/**
 * Everything below this route requires a session.
 *
 * The guard resolves `session.me` through the query client, so a hard refresh on a deep
 * link waits for the real answer instead of flashing the login screen and bouncing back.
 */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient
      .fetchQuery(context.trpc.session.me.queryOptions())
      .catch(() => null);
    if (!session) {
      // TanStack Router signals navigation by throwing its Redirect object; it is not
      // an Error subclass, which is exactly how the router distinguishes it from a failure.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    // A super_admin who has not enrolled TOTP yet is refused by every procedure except
    // `session.me` — there is nothing this half of the app can show them until they do.
    if ((session as SessionUser).twoFactorSetupRequired) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/setup-2fa' });
    }
    return { session: session as SessionUser };
  },
  component: AppShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: DashboardPage,
});

const geoRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/geo',
  component: GeoPage,
});

const referenceRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/reference',
  component: ReferencePage,
});

const brandsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/brands',
  component: BrandsPage,
});

/**
 * Бренд приходить у параметрі, бо вхід на цей екран — із вкладки «Ключі» його ж картки.
 *
 * Мокап тримає імпорт ядра саме там (рядок 3316, 2850), а не окремим пунктом меню; без
 * параметра оператор, який щойно дивився на ключі бренду, мусив би вибрати той самий бренд
 * удруге зі списку на двісті рядків.
 */
const coreSearchSchema = z.object({
  ownerType: z.enum(['brand', 'game', 'cluster']).optional(),
  ownerId: z.string().optional(),
});

export type CoreSearch = z.infer<typeof coreSearchSchema>;

const coreRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/core',
  component: CorePage,
  validateSearch: coreSearchSchema,
});

const gamesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/games',
  component: GamesPage,
});

const clustersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/clusters',
  component: ClustersPage,
});

const sitesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sites',
  component: SitesPage,
});

const auditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/audit',
  component: AuditPage,
});

/**
 * Обидва екрани відкриваються з картки сайту, як у мокапі: збірка — кнопкою на вкладці
 * «Сторінки», редактор — кліком по рядку сторінки. Тому контекст їде в адресі.
 *
 * Досі кнопка «Редактор» у картці вела на `/editor` **без параметрів**: оператор, який щойно
 * дивився на конкретну сторінку конкретного сайту, потрапляв на порожній екран і мусив
 * вибрати обидва вручну. Двері були, але за ними починалося спочатку.
 */
const buildSearchSchema = z.object({ siteId: z.string().optional() });

export type BuildSearch = z.infer<typeof buildSearchSchema>;

const buildRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/build',
  component: BuildPage,
  validateSearch: buildSearchSchema,
});

const editorSearchSchema = z.object({
  siteId: z.string().optional(),
  pageId: z.string().optional(),
});

export type EditorSearch = z.infer<typeof editorSearchSchema>;

const editorRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/editor',
  component: EditorPage,
  validateSearch: editorSearchSchema,
});

/**
 * The bulk screen is reached from the sites registry, not from the sidebar — the mockup has
 * no «Масові операції» nav row, it has a «МАСОВІ ДІЇ · N САЙТІВ» bar that appears once rows
 * are ticked. So the scope arrives in the URL: which domains, and which operation the button
 * that was pressed stands for.
 *
 * In the URL rather than in a store on purpose. An operator who is about to change canonical
 * on forty live domains should be able to send that exact screen to a colleague, and reloading
 * it must not silently drop half the targets.
 */
const bulkSearchSchema = z.object({
  siteIds: z.array(z.string()).optional(),
  operation: z.enum(BULK_OPERATIONS).optional(),
  /**
   * Уже виконана операція, відкрита з журналу картки сайту.
   *
   * Панель «Історія операцій» звідси прибрана — мокап її не малює, а вхід у відкат за планом
   * живе в журналі картки. Але відкотити одну зміну на одному сайті й відкотити операцію на
   * сорока доменах — різні дії; друга лишається досяжною через це посилання.
   */
  operationId: z.string().optional(),
});

export type BulkSearch = z.infer<typeof bulkSearchSchema>;

/**
 * Картка сайту — шість вкладок мокапа за власним маршрутом.
 *
 * Досі вона була панеллю під реєстром: її не можна було надіслати колезі, відкрити з
 * дашборда чи з інциденту, і шість станів мокапа числились ненабудованими, бо жоден маршрут
 * їх не віддавав.
 */
/**
 * Візард нового сайту стоїть **перед** карткою сайту в дереві навмисно: `/sites/new` інакше
 * матчиться як `/sites/$siteId` з ідентифікатором «new», і візард відкривався б як картка
 * неіснуючого сайту.
 */
/**
 * Домен приходить у параметрі, бо ланцюг «купити та створити сайт» починається на іншому
 * екрані: EMD-генератор купує домен і веде сюди. Без параметра оператор мусив би передрукувати
 * щойно куплений домен руками — і одна помилка в літері коштувала б і домена, і сайту.
 */
const newSiteSearchSchema = z.object({ domain: z.string().optional() });

export type NewSiteSearch = z.infer<typeof newSiteSearchSchema>;

const newSiteRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sites/new',
  component: NewSitePage,
  validateSearch: newSiteSearchSchema,
});

const siteCardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sites/$siteId',
  component: SiteCardPage,
});

const bulkRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/bulk',
  component: BulkPage,
  validateSearch: bulkSearchSchema,
});

const monitoringRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/monitoring',
  component: MonitoringPage,
});

const linksRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/links',
  component: LinksPage,
});

const emdRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/emd',
  component: EmdPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings',
  component: SettingsPage,
});

const templatesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/templates',
  component: TemplatesPage,
});

const promptsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/prompts',
  component: PromptsPage,
});

const contentGeneratorRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/content-generator',
  component: LinkContentPage,
});
const usersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/users',
  component: UsersPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  setPasswordRoute,
  setup2faRoute,
  authedRoute.addChildren([
    dashboardRoute,
    geoRoute,
    referenceRoute,
    brandsRoute,
    coreRoute,
    gamesRoute,
    clustersRoute,
    sitesRoute,
    newSiteRoute,
    siteCardRoute,
    auditRoute,
    buildRoute,
    editorRoute,
    bulkRoute,
    monitoringRoute,
    linksRoute,
    emdRoute,
    settingsRoute,
    templatesRoute,
    promptsRoute,
    contentGeneratorRoute,
    usersRoute,
  ]),
]);

export function createAppRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
